/* eslint-disable */
/*!
   _____ ______ __  __ _    _  _____ __  __ ______ _____  
  / ____|  ____|  \/  | |  | |/ ____|  \/  |  ____|  __ \ 
 | |  __| |__  | \  / | |__| | (___ | \  / | |__  | |  | |
 | | |_ |  __| | |\/| |  __  |\___ \| |\/| |  __| | |  | |
 | |__| | |____| |  | | |  | |____) | |  | | |____| |__| |
  \_____|______|_|  |_|_|  |_|_____/|_|  |_|______|_____/                                                                                                                                                                                                                                                                                                   
=========================================================
* GEMHSMED UI - v2.0.0
=========================================================

* Copyright 2024 GEMHS (https://gemhs.net/)

* Designed and Coded by GEMHS

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software. 

*/

// Chakra imports
import {
    Container,
    Box,
    Icon,
    SimpleGrid,
    Spinner,
    useColorModeValue,
    useDisclosure,
    IconButton,
    Tooltip,
    Modal,
    ModalBody,
    ModalHeader,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalOverlay,
    Flex,
    Button,
    Text,
    FormControl,
    Input,
    FormErrorMessage,
    FormLabel,
    Checkbox,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Select
} from "@chakra-ui/react";
import { DeleteIcon, InfoIcon } from "@chakra-ui/icons";
import MiniStatistics from "components/card/MiniStatistics";
import IconBox from "components/icons/IconBox";
import {
    useGetUsersQuery,
    useDeleteUserMutation,
    useCreateUserMutation,
    useGetCurrentUserQuery
} from "store";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import React, { useState, useEffect, useMemo } from "react";
import { 
    MdPeople,
    MdAddCircleOutline
 } from "react-icons/md";
import { Helmet } from "react-helmet-async";


function PaginatedTable({ columns, initialPageSize = 10 }) {
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(initialPageSize);

    const { data: users, isFetching, error } = useGetUsersQuery(
        { page: pageIndex + 1, pageSize },
        {
            keepPreviousData: true,
        }
    );

    const lastPage = Math.ceil(users?.count / pageSize) - 1;
    const currentPage = pageIndex + 1; // Human-readable page count (1-based)

    if (error) return <Text color="red.500">Error: {error.message}</Text>;
    if (isFetching) return <Spinner size="lg" />;

    return (
        <Box overflowX="auto">
            <Table variant="striped" colorScheme="teal">
                <Thead>
                    <Tr>
                        {columns.map((column) => (
                            <Th key={column.id}>{column.header}</Th>
                        ))}
                    </Tr>
                </Thead>
                <Tbody>
                    {users?.results.map((row, i) => (
                        <Tr key={i}>
                            {columns.map((column) => (
                                // <Td key={column.id}>{row[column.id]}</Td>
                                <Td key={column.id}>{typeof column.accessor === 'function' ? column.accessor(row) : row[column.accessor]}</Td>
                            ))}
                        </Tr>
                    ))}
                </Tbody>
            </Table>
            <Flex justifyContent="space-between" alignItems="center" p="4">
                <Button
                    onClick={() => setPageIndex(0)}
                    disabled={pageIndex === 0}
                >
                    {"<<"}
                </Button>
                <Button
                    onClick={() => setPageIndex((old) => Math.max(old - 1, 0))}
                    disabled={pageIndex === 0}
                >
                    {"<"}
                </Button>
                <Text>
                    Page {currentPage} of {lastPage + 1}
                </Text>
                <span>
                    | Go to page:{" "}
                    <Input
                        type="number"
                        value={pageIndex + 1}
                        onChange={(e) => {
                            const page = e.target.value
                                ? Number(e.target.value) - 1
                                : 0;
                            setPageIndex(page);
                        }}
                        style={{ width: "100px" }}
                    />
                </span>
                <Button
                    onClick={() =>
                        setPageIndex((old) => Math.min(old + 1, lastPage))
                    }
                    disabled={pageIndex >= lastPage}
                >
                    {">"}
                </Button>
                <Button
                    onClick={() => setPageIndex(lastPage)}
                    disabled={pageIndex >= lastPage}
                >
                    {">>"}
                </Button>
                <Select
                    ml="4"
                    width="auto"
                    value={pageSize}
                    onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setPageIndex(0); // Reset to the first page after changing page size
                    }}
                >
                    {[5, 15, 30, 50].map((size) => (
                        <option key={size} value={size}>
                            Show {size}
                        </option>
                    ))}
                </Select>
            </Flex>
        </Box>
    );
}

function Users() {
    const { data: users, isFetching, err } = useGetUsersQuery({
        page: 1,
        pageSize: 5
    });
    const userCount = users ? users.count : 0;

    const {
        data: currentUser,
        error: errs,
        isLoading
    } = useGetCurrentUserQuery();
    
    const navigate = useNavigate();
    const [deleteUser, { isLoading: isDeleting }] = useDeleteUserMutation();
    const [userToDelete, setUserToDelete] = useState(null);
    const [createUser, results] = useCreateUserMutation();

    const handleDetailsClick = (userId) => {
        navigate(`/user/${userId}/details`);
    };

    const handleDeleteClick = (userId) => {
        setUserToDelete(userId);
        onOpen();
    };

    const confirmDelete = async () => {
        try {
            if (userToDelete) {
                await deleteUser(userToDelete);
                setUserToDelete(null);
                onClose();
            }
        } catch (error) {
            console.error("Error deleting user:", error);
        }
    };

    const onSubmit = async (data) => {
        try {
            await createUser(data);
            reset();
            handleClose();
        } catch (error) {
            console.error("Error creating user:", error);
        }
    };

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm();

    const { isOpen, onOpen, onClose } = useDisclosure();
    const [show, setShow] = useState(false);

    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);

    const columns = useMemo(() => [
        {
            id: "username",
            header: "Username",
            accessor: (row) => row.username,
        },
        {
            id: "role",
            header: "Role",
            accessor: (row) => row.is_hospital_admin ? "Admin" : "User"
        },
        // {
        //     id: "login_status",
        //     header: "Session",
        //     accessor: (row) => (
        //         <Box display="flex" alignItems="center">
        //             <Box
        //                 height="10px"
        //                 width="10px"
        //                 borderRadius="50%"
        //                 backgroundColor={row.login_status ? 'green': 'red'}
        //                 marginRight="8px"
        //             />
        //             {row.login_status ? 'Online' : 'Offline'}
        //         </Box>
        //     ),
        // },
        {
            id: "last_login",
            header: "Last Login",
            accessor: (row) => {
                const date = new Date(row.last_login);
                return !row.last_login ? "No Data" :
                    isNaN(date.getTime()) ? "Invalid Date" :
                    new Intl.DateTimeFormat("en-US", {
                        timeZone: "America/St_Vincent",
                        dateStyle: "medium",
                        timeStyle: "short",
                    }).format(date);
            }
        },
        {
            id: "actions",
            header: "Actions",
            accessor: (row) => (
                <Box>
                    <Tooltip label="View details" aria-label="View details tooltip">
                        <IconButton
                            aria-label="View details"
                            icon={<InfoIcon />}
                            onClick={() => handleDetailsClick(row.id)}
                            mr="2"
                        />
                    </Tooltip>
                    <Tooltip label="Delete" aria-label="Delete tooltip">
                        <IconButton
                            aria-label="Delete"
                            icon={<DeleteIcon />}
                            onClick={() => handleDeleteClick(row.id)}
                            colorScheme="red"
                        />
                    </Tooltip>
                </Box>
            ),
        }
    ], []);

    // Chakra Color Mode
    const brandColor = useColorModeValue("brand.500", "white");
    const boxBg = useColorModeValue("secondaryGray.300", "whiteAlpha.100");
    const [scrollBehavior, setScrollBehavior] = React.useState("inside");
    return (
        <>
            <Helmet>
                <title>Users</title>
            </Helmet>

            <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
                <SimpleGrid
                    columns={{ base: 1, md: 2, lg: 3, "2xl": 6 }}
                    gap="20px"
                    mb="20px"
                >
                    <MiniStatistics
                        startContent={
                            <IconBox
                                w="56px"
                                h="56px"
                                bg={boxBg}
                                icon={
                                    <Icon
                                        w="32px"
                                        h="32px"
                                        as={MdPeople}
                                        color={brandColor}
                                    />
                                }
                            />
                        }
                        name="Users"
                        value={
                            isFetching ? (
                                <Spinner size="lg" />
                            ) : err ? (
                                <Text>Error fetching users</Text>
                            ) : (
                                userCount
                            )
                        }
                    />
                </SimpleGrid>

                {/* Delete Modal */}

                <Modal isOpen={isOpen} onClose={onClose}>
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>Confirm Deletion</ModalHeader>
                        <ModalBody>
                            <Text>
                                Are you sure you want to delete this user?
                            </Text>
                        </ModalBody>
                        <ModalFooter>
                            <Button colorScheme="blue" mr={3} onClick={onClose}>
                                Cancel
                            </Button>
                            <Button
                                colorScheme="red"
                                onClick={confirmDelete}
                                isLoading={isDeleting}
                            >
                                Delete
                            </Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>

                <Flex justify="flex-end" mb="4">
                    <Button leftIcon={<MdAddCircleOutline />} isLoading={results.isLoading} onClick={handleShow}>
                        New User
                    </Button>
                </Flex>

                {/* Create Modal */}

                <Modal
                    isOpen={show}
                    onClose={handleClose}
                    isCentered
                    {...users}
                    closeOnOverlayClick={false}
                    closeOnEsc={false}
                    scrollBehavior={scrollBehavior}
                    size="2xl"
                >
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>New User</ModalHeader>
                        <ModalCloseButton />
                        <ModalBody>
                            <form onSubmit={handleSubmit(onSubmit)}>
                                {/* Username */}
                                <Box mb={4}>
                                    <FormControl isInvalid={errors.username}>
                                        <FormLabel>Username</FormLabel>
                                        <Input
                                            type="text"
                                            {...register("username", {
                                                required:
                                                    "Username is required",
                                            })}
                                        />
                                        <FormErrorMessage>
                                            {errors.username?.message}
                                        </FormErrorMessage>
                                    </FormControl>
                                </Box>

                                {/* Password */}
                                <Box mb={4}>
                                    <FormControl isInvalid={errors.password}>
                                        <FormLabel>Password</FormLabel>
                                        <Input
                                            type="password"
                                            {...register("password", {
                                                required:
                                                    "Password is required",
                                            })}
                                        />
                                        <FormErrorMessage>
                                            {errors.password?.message}
                                        </FormErrorMessage>
                                    </FormControl>
                                </Box>

                                {/* Confirm Password */}
                                <Box mb={4}>
                                    <FormControl isInvalid={errors.password2}>
                                        <FormLabel>Confirm Password</FormLabel>
                                        <Input
                                            type="password"
                                            {...register("password2", {
                                                required:
                                                    "Confirm password is required",
                                                validate: (value, data) =>
                                                    value === data.password ||
                                                    "Passwords do not match",
                                            })}
                                        />
                                        <FormErrorMessage>
                                            {errors.password2?.message}
                                        </FormErrorMessage>
                                    </FormControl>
                                </Box>

                                {/* is_hospital_admin */}
                                <Box mb={4}>
                                    <FormControl
                                        isInvalid={errors.is_hospital_admin}
                                    >
                                        <Checkbox
                                            {...register("is_hospital_admin")}
                                        >
                                            Admin
                                        </Checkbox>
                                        <FormErrorMessage>
                                            {errors.is_hospital_admin?.message}
                                        </FormErrorMessage>
                                    </FormControl>
                                </Box>

                                {/* is_receptionist */}
                                <Box mb={4}>
                                    <FormControl
                                        isInvalid={errors.is_receptionist}
                                    >
                                        <Checkbox
                                            {...register("is_receptionist")}
                                        >
                                            Receptionist
                                        </Checkbox>
                                        <FormErrorMessage>
                                            {errors.is_receptionist?.message}
                                        </FormErrorMessage>
                                    </FormControl>
                                </Box>

                                <SimpleGrid>
                                    <Box>
                                        <Button
                                            fontSize="sm"
                                            variant="outline"
                                            fontWeight="500"
                                            type="submit"
                                            w="20%"
                                            h="50"
                                            mb="24px"
                                            isLoading={results.isLoading}
                                            loadingText="Loading..."
                                            spinner={<Spinner />}
                                        >
                                            Save
                                        </Button>
                                    </Box>
                                </SimpleGrid>
                            </form>
                        </ModalBody>
                    </ModalContent>
                </Modal>

                {/* Table */}

                <Container maxW="full" p={4}>
                    <Box w="full">
                        <SimpleGrid columns={1} gap="20px" mb="20px">
                            {isFetching ? (
                                <Box display="flex" justifyContent="center">
                                    <Spinner size="xl" />
                                </Box>
                            ) : err ? (
                                <Box>Error fetching user data</Box>
                            ) : (
                                <PaginatedTable
                            columns={columns}
                            initialPageSize={5}
                        />
                            )}
                        </SimpleGrid>
                    </Box>
                </Container>
            </Box>
        </>
    );
}

export default Users;
